import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import ProductCombobox from '@/components/ProductCombobox';
import MerkezSecici from '@/components/MerkezSecici';
import { ArrowLeft, Save, Plus, X, AlertTriangle, Upload, Sparkles, Inbox, RotateCcw, Package } from 'lucide-react';
import { toast } from 'sonner';
import { birimSecenekleri } from '@/lib/birimler';
import { trNorm } from '@/lib/arama';
import { satirTutari, ambalajiNormallestir, ilkDepoyuYay, faturaToplamlari } from '@/lib/faturaKalemi';

/**
 * SATIŞ FATURASI — ALIŞ FATURASININ (`invoices/InvoiceNew.js`) DÜZENİYLE.
 *
 * Kullanıcı (2026-10-01): "Satış faturaları alış faturalarının aynısı olsun
 * demiştik ama şu anda değil". Ekran alışın ESKİ bir kopyasıydı: ambalaj
 * birimi / içeriği yoktu (satırda serbest "Birim" kutusu vardı, stoktan ne
 * kadar çıkacağı görünmüyordu) ve depo hem fatura başlığında hem kalemde
 * soruluyordu — iki yer, hangisinin geçerli olduğu belirsizdi.
 *
 * Artık:
 * - **AMBALAJ ÜÇLÜSÜ** alıştaki gibi: AMB. ADEDİ (`quantity`), AMB. BİRİMİ
 *   (`unit`, üründe tanımlı birimler öne çıkar ve içeriği karttan doldurur),
 *   AMB. İÇERİĞİ (`package_qty`, ANA BİRİMDE). Sunucunun stok çıkışı zaten bu
 *   üçlüyle hesaplıyordu (`satis._stok_cikisi`); ekran onu söyletemiyordu.
 * - **DEPO YALNIZCA KALEMDE.** Başlıkta yalnızca kullanılan depoların özeti
 *   durur. İlk kalemin deposu diğer satırları doldurur (ortak kural).
 *   Eski faturalarda başlık deposu kalemlere taşınarak yüklenir.
 * - Satır tutarı, ambalaj normalleştirmesi ve depo yayılımı alışla TEK
 *   kaynaktan (`lib/faturaKalemi`) — kopya bir daha ayrışmasın.
 *
 * Satışa ÖZGÜ kalanlar: müşteri (cari), fatura türleri (sunucudan), numara
 * önerisi, kalem türü yok (ürün eşleşmeyen satır HİZMETTİR, stoka dokunmaz),
 * KDV oranı karta YAZILMAZ (satışta ne `kdv_karta_yaz` ne `kdv_secildi`
 * gönderilir — müşteriye satılanın oranı kartın tanımını değiştirmemeli),
 * fiyat sapma uyarısı yok (alış fiyatıyla kıyas satışta anlamsız).
 */

const PARA_BIRIMLERI = ['TRY', 'USD', 'EUR', 'GBP'];
const KDV_ORANLARI = ['0', '1', '5', '8', '10', '16', '18', '20'];
const BOS_KALEM = {
  description: '', quantity: '1', unit: '', package_qty: '', package_unit: '',
  unit_price: '', discount_rate: '', kdv_rate: '', matched_product_id: '',
  warehouse_id: '',
};

const bugun = () => new Date().toLocaleDateString('en-CA');
const fmtTR = (n) => (Number(n) || 0).toLocaleString('tr-TR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── Kalem tablosu sınıfları — alış faturasıyla AYNI ───
// Mobilde hücreler blok olur (tablo yatay kaymaz), masaüstünde hücre kalır.
const HUCRE = 'block md:table-cell px-1.5 py-1 md:py-1.5 align-top';
const ALAN_ETIKET = 'flex items-center gap-1 h-4 text-[10px] font-semibold '
  + 'text-[#8a7e70] uppercase mb-1 leading-none';
const ETIKET = 'md:hidden block text-[10px] font-semibold text-[#8a7e70] uppercase mb-0.5';
const KONTROL = 'h-11 md:h-8 rounded-md border bg-white text-base md:text-[13px] text-[#1a1916] '
  + 'focus:outline-none focus:border-[#3d6e4e] focus:ring-2 focus:ring-[#3d6e4e]/20 transition-shadow';
const GIRDI = `${KONTROL} border-[#d0c4b0] w-full min-w-0 px-1.5`;
const SAYI = `${GIRDI} text-right font-mono tabular-nums`;
const SECIM = `${KONTROL} border-[#d0c4b0] w-full min-w-0 pl-1.5 pr-0.5`;
const IPUCU = 'block mt-0.5 text-[10px] font-mono tabular-nums text-right leading-tight';
const BASLIK = 'text-[#1a1916] font-semibold text-[11px] uppercase tracking-wide px-1.5 py-2 leading-tight';
const GRUP = 'border-l border-[#e6dcc9]';
const UST = 'bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0';

export default function SatisFaturaNew() {
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/satis-faturalari');
  const { id } = useParams();
  const duzenleme = Boolean(id);
  const { products } = useProducts();
  // ── DOSYA KUTUSUNDAN GELEN BELGE (kullanıcı, 2026-10-01) ──
  // Bir dönem kutudaki her fatura alış ekranına gidiyordu. Akış alış
  // faturasıyla aynı: belge KİLİTLENİR (iki kişi aynı belgeyi işlemesin),
  // tüm sayfaları sunucu okur, fatura KAYDEDİLİNCE "işlendi"ye taşınır;
  // vazgeçilirse kutuya geri bırakılır. Alıştaki yarım iş saklama (calisma)
  // burada YOK: sayfadan çıkılırsa yazılanlar gider, belge "Devam Et" ile
  // yeniden okunur.
  const [searchParams] = useSearchParams();
  const kutuBelgeParam = searchParams.get('kutu_belge');
  const [kutuBelgesi, setKutuBelgesi] = useState(null);
  const kutuAcildi = useRef(false);

  const [cariler, setCariler] = useState([]);
  const [depolar, setDepolar] = useState([]);
  const [units, setUnits] = useState([]);   // Amb. Birimi listesi ve metrik çevrim
  // Tur listesi SUNUCUDAN: iki yerde tutulan liste ayrisirdi (iade yonu
  // sunucuda belirleniyor, ekran onu uydurmamali).
  const [turler, setTurler] = useState([]);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [uyarilar, setUyarilar] = useState([]);
  // ── Görsel + AI okuma (alış faturasındaki akışın aynısı) ──
  const [okunuyor, setOkunuyor] = useState(false);
  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  const [gorselAcik, setGorselAcik] = useState(false);
  const [surukleniyor, setSurukleniyor] = useState(false);
  const [gorsel, setGorsel] = useState(null);
  // AI'nin okuduğu HAM isim — cari bağlantısı DEĞİLDİR, yalnızca metin.
  const [okunanMusteri, setOkunanMusteri] = useState('');
  const dosyaRef = useRef(null);
  const [form, setForm] = useState({
    customer_id: '', invoice_number: '', invoice_date: bugun(),
    due_days: '', due_date: '', currency: 'TRY', exchange_rate: '1',
    cost_center_id: '', kdv_inclusive: false,
    notes: '', status: 'completed', invoice_type: 'fatura',
  });
  const [kalemler, setKalemler] = useState([{ ...BOS_KALEM }]);

  useEffect(() => {
    api.get('/suppliers').then(r => {
      const liste = r.data?.items || r.data || [];
      // Kasa/banka hesapları müşteri OLAMAZ — onlar para hesabı, cari değil.
      // /suppliers her para birimi için ayrı satır döndürüyor; tekilleştir.
      const gorulen = new Set();
      setCariler(liste.filter(s => s.account_type !== 'Kasa ve Bankalar'
        && !gorulen.has(s.id) && gorulen.add(s.id)));
    }).catch(() => {});
    api.get('/warehouses').then(r => setDepolar(r.data || [])).catch(() => {});
    api.get('/admin/units').then(r => setUnits(r.data || [])).catch(() => {});
    api.get('/satis-faturalari/turler').then(r => setTurler(r.data || [])).catch(() => {});
  }, []);

  // Yeni faturada numarayı sunucu önerir; kullanıcı üzerine yazabilir
  // (e-Fatura numarası dışarıdan gelebiliyor).
  useEffect(() => {
    if (duzenleme) return;
    api.get('/satis-faturalari/siradaki-no', { params: { yil: Number(form.invoice_date.slice(0, 4)) } })
      .then(r => setForm(p => (p.invoice_number ? p : { ...p, invoice_number: r.data.numara })))
      .catch(() => {});
  }, [duzenleme, form.invoice_date]);

  useEffect(() => {
    if (!id) return;
    api.get(`/satis-faturalari/${id}`).then(r => {
      const f = r.data;
      setForm({
        customer_id: f.customer_id || '', invoice_number: f.invoice_number || '',
        invoice_date: (f.invoice_date || '').slice(0, 10) || bugun(),
        due_days: f.due_days ?? '', due_date: (f.due_date || '').slice(0, 10),
        currency: f.currency || 'TRY', exchange_rate: String(f.exchange_rate ?? '1'),
        cost_center_id: f.cost_center_id || '',
        kdv_inclusive: Boolean(f.kdv_inclusive), notes: f.notes || '',
        status: f.status || 'completed', invoice_type: f.invoice_type || 'fatura',
      });
      setGorsel(f.image_data || null);
      setKalemler((f.items || []).length
        ? f.items.map(i => ({
          description: i.description || '', quantity: String(i.quantity ?? ''),
          unit: i.unit || '', package_qty: i.package_qty != null ? String(i.package_qty) : '',
          package_unit: i.package_unit || '',
          unit_price: String(i.unit_price ?? ''),
          discount_rate: i.discount_rate != null ? String(i.discount_rate) : '',
          kdv_rate: i.kdv_rate != null ? String(i.kdv_rate) : '',
          matched_product_id: i.matched_product_id || '',
          // Eski faturalarda depo BAŞLIKTAYDI; kalem boşsa o kaleme taşınır.
          // Kayıtta depo artık yalnızca kalemde gönderilir.
          warehouse_id: i.warehouse_id || (i.matched_product_id ? f.warehouse_id : '') || '',
        }))
        : [{ ...BOS_KALEM }]);
    }).catch(() => {
      toast.error('Satış faturası bulunamadı');
      navigate('/erp/satis-faturalari');
    });
  }, [id, navigate]);

  // Ambalaj içeriği ANA BİRİMDE — alışla aynı kural (lib/faturaKalemi)
  useEffect(() => {
    if (!products.length || !units.length) return;
    setKalemler(prev => ambalajiNormallestir(prev, products, units));
  }, [kalemler, products, units]);

  const satirKarti = (k) => products.find(p => p.id === k.matched_product_id) || null;

  // Yeni kalemde son kullanılan depo varsayılan gelir (alışla aynı)
  const sonDepo = useMemo(() => {
    for (let i = kalemler.length - 1; i >= 0; i--) {
      if (kalemler[i].warehouse_id) return kalemler[i].warehouse_id;
    }
    return '';
  }, [kalemler]);

  // Faturada fiilen kullanılan depolar — başlıktaki özet
  const kullanilanDepolar = useMemo(() => {
    const ids = [...new Set(kalemler.filter(k => k.matched_product_id && k.warehouse_id)
      .map(k => k.warehouse_id))];
    return ids.map(x => depolar.find(w => w.id === x)).filter(Boolean);
  }, [kalemler, depolar]);

  const kalemDegistir = (i, alan, deger) => {
    setKalemler(prev => {
      const liste = (i === 0 && alan === 'warehouse_id') ? ilkDepoyuYay(prev, deger) : [...prev];
      const yeni = { ...liste[i], [alan]: deger };
      if (alan === 'matched_product_id') {
        const p = products.find(x => x.id === deger);
        if (p) {
          yeni.description = yeni.description || p.name || '';
          // Elle açılan satırda birim boşsa ana birimle başlar; ürün seçmek
          // kullanıcının bilinçli eylemi. (AI okumasındaki boş birim burada
          // doldurulmaz — o satırın ürünü zaten eşleşmiş gelir.)
          yeni.unit = yeni.unit || p.unit || '';
          yeni.package_unit = p.unit || '';
          // SATIŞTA KDV VARSAYILANI KART (firma son alımı satışta kullanılmaz:
          // müşteriye satılanın oranı alıştakiyle aynı olmayabilir).
          if (p.kdv_rate != null && p.kdv_rate !== '') yeni.kdv_rate = String(p.kdv_rate);
          if (!yeni.warehouse_id) yeni.warehouse_id = liste[0]?.warehouse_id || sonDepo || '';
        } else {
          // Eşleşme kaldırıldı → HİZMET satırı: stoka dokunmaz, deposu da yok.
          yeni.package_unit = '';
        }
      }
      // AMBALAJ İÇERİĞİ ürün kartındaki dönüşümden gelir (alışla aynı):
      // tanımlı birim seçilince elle yazmaya gerek yok, ana birimde içerik 1.
      if (alan === 'unit' || alan === 'matched_product_id') {
        const kart = products.find(x => x.id === yeni.matched_product_id);
        if (kart && yeni.unit) {
          const es = birimSecenekleri(kart, units).find(o => trNorm(o.deger) === trNorm(yeni.unit));
          if (es && es.carpan > 0) yeni.package_qty = String(es.carpan);
        }
      }
      liste[i] = yeni;
      return liste;
    });
  };

  const kalemEkle = () => setKalemler(p => [...p, { ...BOS_KALEM, warehouse_id: sonDepo }]);

  // ── AI ile oku ──
  const dosyaOku = async (e, dogrudanDosya) => {
    const dosya = dogrudanDosya || e?.target?.files?.[0];
    if (!dosya) return;
    setYuklemeAcik(false);
    setOkunuyor(true);
    try {
      const veri = new FormData();
      veri.append('file', dosya);
      const r = await api.post('/satis-faturalari/parse', veri,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      okumaSonucunuIsle(r.data);
    } catch (err) {
      toast.error('Belge okunamadı: ' + (err.response?.data?.detail || err.message));
    }
    setOkunuyor(false);
    if (dosyaRef.current) dosyaRef.current.value = '';
  };

  useEffect(() => {
    if (duzenleme || !kutuBelgeParam || kutuAcildi.current || !products.length) return;
    kutuAcildi.current = true;
    (async () => {
      setOkunuyor(true);
      try {
        const b = (await api.get(`/dosya-kutusu/${kutuBelgeParam}`)).data;
        await api.post(`/dosya-kutusu/${b.id}/kilitle`);
        setKutuBelgesi(b);
        const r = await api.post(`/satis-faturalari/parse-belge/${b.id}`);
        okumaSonucunuIsle(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Belge okunamadı');
      } finally {
        setOkunuyor(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kutuBelgeParam, products.length, duzenleme]);

  const kutuyaGeriBirak = async () => {
    if (!kutuBelgesi) return;
    try {
      await api.post(`/dosya-kutusu/${kutuBelgesi.id}/birak`);
      toast.info('Belge kutuya geri bırakıldı');
    } catch { /* yoksay */ }
    setKutuBelgesi(null);
    navigate('/erp/dosya-kutusu');
  };

  // Belge ANCAK kayıt başarıyla oluştuktan sonra işlenmişe geçer
  const kutuyuKapat = async (kayit) => {
    if (!kutuBelgesi || !kayit?.id) return;
    try {
      await api.post(`/dosya-kutusu/${kutuBelgesi.id}/islendi`, {
        kayit_turu: 'satis_faturasi', kayit_id: kayit.id, kayit_no: kayit.invoice_number || '' });
      toast.success('Belge dosya kutusundan kaldırıldı');
      setKutuBelgesi(null);
    } catch {
      toast.warning('Fatura kaydedildi ama belge kutuda kaldı — elle işaretleyin');
    }
  };

  const okumaSonucunuIsle = (d) => {
    setOkunanMusteri(d.customer_name || '');
    setForm(p => ({
      ...p,
      // Cari BAĞLANTISI yalnızca sunucu eşleştirdiyse kurulur; okunan isim
      // eşleşmediyse alan boş kalır ve kaydetme koruması kullanıcıyı gerçek
      // cariyi seçmeye zorlar (alış tarafındaki kuralın aynısı).
      customer_id: d.matched_customer_id || p.customer_id,
      invoice_number: d.invoice_number || p.invoice_number,
      invoice_date: d.invoice_date || p.invoice_date,
      due_days: d.due_date_days ? String(d.due_date_days) : p.due_days,
      currency: d.currency || p.currency,
      // KDV dahil/hariç önerisi bu müşterinin GEÇMİŞ satış faturalarından
      // gelir; öneri yoksa kullanıcının seçimi korunur.
      kdv_inclusive: d.kdv_inclusive_onerisi == null
        ? p.kdv_inclusive : !!d.kdv_inclusive_onerisi,
    }));
    const okunan = d.items || [];
    // Okunan kalemler, ekranda o an seçili ilk deponun altında açılır
    setKalemler(prev => {
      const depo = prev[0]?.warehouse_id || '';
      return okunan.length ? okunan.map(it => ({
        description: it.description || '',
        quantity: it.quantity != null ? String(it.quantity) : '1',
        unit: it.unit || '',
        package_qty: it.package_qty != null ? String(it.package_qty) : '',
        package_unit: it.package_unit || '',
        unit_price: it.unit_price != null ? String(it.unit_price) : '',
        discount_rate: it.discount_rate != null ? String(it.discount_rate) : '',
        // KDV oranı ÜRÜN KARTINDAN gelir (sunucu dolduruyor), belgeden okunmaz.
        kdv_rate: it.kdv_rate != null ? String(it.kdv_rate) : '',
        matched_product_id: it.matched_product_id || '',
        warehouse_id: it.matched_product_id ? depo : '',
      })) : [{ ...BOS_KALEM }];
    });
    if (d.image_data) setGorsel(d.image_data);
    const eslesen = okunan.filter(x => x.matched_product_id).length;
    const sayfa = d.sayfa_sayisi > 1 ? `${d.sayfa_sayisi} sayfa okundu — ` : '';
    toast.success(`${sayfa}${eslesen}/${okunan.length} kalem eşleşti`);
  };

  // KDV dahil/hariç — alış faturasındaki `calcKdv` ile aynı kural
  const kdvAyir = useCallback((tutar, oran) => {
    const t = Number(tutar) || 0;
    const o = parseFloat(oran) || 0;
    if (form.kdv_inclusive) {
      const net = o ? t / (1 + o / 100) : t;
      return { net, kdv: t - net };
    }
    return { net: t, kdv: t * o / 100 };
  }, [form.kdv_inclusive]);

  // Iade YON DEGISTIRMEZ (mal yine cikar, bakiye yine arti yonde); yalnizca
  // aciklama metni ve defterdeki hareket turu farkli. Bayrak sunucudan gelir.
  const iadeMi = (turler.find(t => t.deger === form.invoice_type) || {}).iade === true;

  const doluKalemler = kalemler.filter(k => (parseFloat(k.quantity) || 0) > 0
    && (k.description.trim() || k.matched_product_id));
  const brutToplam = doluKalemler.reduce((s, k) => s + satirTutari(k).brut, 0);
  const iskontoToplam = doluKalemler.reduce((s, k) => s + satirTutari(k).iskonto, 0);
  // Toplamlar kuruşlu ve alışla aynı kuraldan (lib/faturaKalemi.faturaToplamlari)
  const { matrah, kdv: kdvToplam, genel: genelToplam } = faturaToplamlari(
    doluKalemler, (k) => k.kdv_rate, form.kdv_inclusive, (k) => satirTutari(k).net);

  const kaydet = async (durum) => {
    if (durum === 'completed' && !form.customer_id) {
      toast.error('Müşteri cari hesabı seçin');
      return;
    }
    if (!doluKalemler.length) {
      toast.error('En az bir kalem girin');
      return;
    }
    // Stoklu kalemde AMB. BİRİMİ boş bırakılamaz — sunucu da reddediyor
    // (`_validate_stock_units`), burada kaydetmeden önce adıyla söylüyoruz.
    if (durum === 'completed') {
      const birimsiz = doluKalemler.filter(k => k.matched_product_id && !(k.unit || '').trim());
      if (birimsiz.length) {
        toast.error(`Ambalaj birimi girilmemiş ${birimsiz.length} kalem var: `
          + birimsiz.map(k => k.description || satirKarti(k)?.name).slice(0, 3).join(', '));
        return;
      }
    }
    setKaydediliyor(true);
    setUyarilar([]);
    try {
      const govde = {
        ...(duzenleme ? { id } : {}),
        ...form, status: durum,
        due_days: form.due_days === '' ? null : Number(form.due_days),
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        amount_foreign: genelToplam,
        image_data: gorsel,
        // Depo YALNIZCA KALEMDE. Başlık deposu gönderilmez: sunucu deposuz
        // kalemi başlığa düşürüyordu, ekranda görünmeyen bir depodan stok çıkardı.
        warehouse_id: null,
        items: doluKalemler.map(k => ({
          description: k.description, quantity: parseFloat(k.quantity) || 0,
          unit: k.unit || null,
          package_qty: k.package_qty === '' ? null : parseFloat(k.package_qty),
          package_unit: k.package_unit || null,
          unit_price: parseFloat(k.unit_price) || 0,
          discount_rate: k.discount_rate === '' ? 0 : parseFloat(k.discount_rate),
          total_price: satirTutari(k).net,
          kdv_rate: k.kdv_rate === '' ? 0 : parseFloat(k.kdv_rate),
          matched_product_id: k.matched_product_id || null,
          warehouse_id: k.matched_product_id ? (k.warehouse_id || null) : null,
        })),
      };
      const r = duzenleme
        ? await api.patch(`/satis-faturalari/${id}`, govde)
        : await api.post('/satis-faturalari', govde);
      const stokUyari = r.data?.stok_uyarilari || [];
      setUyarilar(stokUyari);
      await kutuyuKapat(r.data);
      toast.success(durum === 'draft' ? 'Taslak kaydedildi' : 'Satış faturası kaydedildi');
      if (!stokUyari.length) navigate('/erp/satis-faturalari');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <div data-testid="satis-fatura-form">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="ghost" onClick={geri}
          className="text-[#8a7e70] hover:text-[#1a1916]"><ArrowLeft size={18} /></Button>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1a1916] tracking-tight">
          {duzenleme ? 'Satış Faturasını Düzenle' : 'Yeni Satış Faturası'}
        </h1>
        <div className="flex-1" />
        {/* Okuma yalnızca YENİ faturada: girilmiş bir faturayı yeniden okutmak
            kullanıcının elle düzelttiği her şeyi silerdi. */}
        {!duzenleme && (
          <>
            <Button onClick={() => setYuklemeAcik(true)} disabled={okunuyor} size="sm"
              className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] text-xs font-semibold h-9 px-4"
              data-testid="satis-ai-oku">
              {okunuyor
                ? <><Sparkles size={14} className="mr-1.5 animate-spin" />Okunuyor...</>
                : <><Upload size={14} className="mr-1.5" />AI ile Oku</>}
            </Button>
            <input ref={dosyaRef} type="file" className="hidden"
              accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf"
              onChange={dosyaOku} data-testid="satis-dosya" />
          </>
        )}
      </div>

      {kutuBelgesi && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-[#2e6fa0]/10 border border-[#2e6fa0]/30 rounded-lg px-3 py-2 text-xs text-[#2e6fa0]"
          data-testid="satis-kutu-belgesi">
          <Inbox size={14} />
          <span className="font-semibold">Dosya kutusundan işleniyor</span>
          <span className="text-[#3F3E39]">
            {kutuBelgesi.sayfa_sayisi > 1 ? `${kutuBelgesi.sayfa_sayisi} sayfa · ` : ''}
            fatura kaydedilince belge "işlendi"ye taşınır
          </span>
          <div className="flex-1" />
          <button type="button" onClick={kutuyaGeriBirak}
            className="inline-flex items-center gap-1 hover:underline" data-testid="satis-kutuya-birak">
            <RotateCcw size={12} /> Kutuya geri bırak
          </button>
        </div>
      )}

      {/* Belge önizlemesi — hem AI okumasından hem kayıtlı faturadan gelir */}
      {gorsel && (
        <div className="mb-3 flex flex-col sm:flex-row items-start gap-2 sm:gap-3 bg-[#f5f0e8] border border-[#d0c4b0] rounded-lg p-2">
          <img src={gorsel} alt="Satış belgesi" onClick={() => setGorselAcik(true)}
            className="max-h-40 sm:max-h-24 w-full sm:w-auto rounded border border-[#d0c4b0] object-contain cursor-zoom-in hover:opacity-80 transition-opacity" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#3d6e4e]">Belge görseli</p>
            {okunanMusteri && (
              <p className="text-xs text-[#8a7e70] truncate">Okunan müşteri: {okunanMusteri}</p>
            )}
            <button type="button" onClick={() => setGorselAcik(true)}
              className="text-xs text-[#3d6e4e] hover:underline mt-0.5">Büyüt →</button>
          </div>
          <button type="button" onClick={() => setGorsel(null)}
            className="text-[#8a7e70] hover:text-[#7a3535] p-1" title="Görseli kaldır"
            data-testid="satis-gorsel-kaldir"><X size={14} /></button>
        </div>
      )}

      {uyarilar.length > 0 && (
        <div className="mb-3 rounded-lg border border-[#7a6a2e]/40 bg-[#7a6a2e]/5 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#7a6a2e]">
            <AlertTriangle size={16} /> Fatura kaydedildi — stok eksiye düştü
          </p>
          {/* Engellemiyoruz: sayımı geride kalmış bir depo yüzünden gerçek
              satışın kaydedilememesi daha kötü. Ama sessiz de geçmiyoruz. */}
          <ul className="mt-1 text-xs text-[#3F3E39] list-disc pl-5">
            {uyarilar.map((u, i) => (
              <li key={i}>{u.urun} · {u.depo}: {u.mevcut} → <b>{u.sonuc}</b></li>
            ))}
          </ul>
          <Button size="sm" variant="outline" className="mt-2 border-[#d0c4b0]"
            onClick={() => navigate('/erp/satis-faturalari')}>Listeye dön</Button>
        </div>
      )}

      <div className="space-y-3">
        {/* ── Kompakt Fatura Bilgileri (alışla aynı iki satır) ── */}
        <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-3">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2 mb-2">
            <div className="col-span-2 md:col-span-4 min-w-0">
              <p className={ALAN_ETIKET}>Müşteri *</p>
              {/* Ekranda AI'nin okuduğu ham isim görünse bile bu gerçek bir
                  cari bağlantısı DEĞİLDİR; kullanıcı listeden seçmeli. */}
              {okunanMusteri && !form.customer_id && (
                <p className="text-[10px] text-[#7a6a2e] mb-0.5">AI: "{okunanMusteri}" — listeden gerçek cariyi seçin</p>
              )}
              <SearchableSelect
                value={form.customer_id}
                options={cariler.map(s => ({ value: s.id, label: s.name, hint: s.code }))}
                onChange={(v) => setForm(p => ({ ...p, customer_id: v }))}
                placeholder="Müşteri cari hesabı seçin"
                searchPlaceholder="Cari ara..."
                className="h-11 md:h-9"
                testId="satis-musteri" />
            </div>
            <div className="col-span-2 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Fatura Türü</p>
              <Select value={form.invoice_type}
                onValueChange={(v) => setForm(p => ({ ...p, invoice_type: v }))}>
                <SelectTrigger className={UST} data-testid="satis-tur"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(turler.length ? turler : [{ deger: 'fatura', etiket: 'Fatura' }])
                    .map(t => <SelectItem key={t.deger} value={t.deger}>{t.etiket}</SelectItem>)}
                </SelectContent>
              </Select>
              {iadeMi && (
                <p className="mt-1 text-[10px] text-[#7a6a2e] leading-tight">
                  Bizim iade ettiğimiz mal: stoktan <b>çıkar</b>, o firmaya olan
                  <b> borcumuz azalır</b>.
                </p>
              )}
            </div>
            <MerkezSecici
              value={form.cost_center_id}
              onChange={(v) => setForm(p => ({ ...p, cost_center_id: v }))}
              label="Sorumluluk Merkezi"
              className="col-span-2 md:col-span-3"
              etiketClass={ALAN_ETIKET}
              tetikClass={UST}
            />
            <div className="col-span-2 md:col-span-3 min-w-0">
              <p className={ALAN_ETIKET}>Fatura No *</p>
              <Input value={form.invoice_number}
                onChange={(e) => setForm(p => ({ ...p, invoice_number: e.target.value }))}
                title="Sıradaki numara önerildi, değiştirebilirsiniz"
                className={UST} data-testid="satis-no" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Fatura Tarihi *</p>
              <Input type="date" value={form.invoice_date}
                onChange={(e) => setForm(p => ({ ...p, invoice_date: e.target.value }))}
                className={UST} data-testid="satis-tarih" />
            </div>
            <div className="col-span-1 md:col-span-1 min-w-0">
              <p className={ALAN_ETIKET}>Vade (gün)</p>
              <Input type="number" step="1" value={form.due_days} placeholder="30"
                onChange={(e) => setForm(p => ({ ...p, due_days: e.target.value, due_date: '' }))}
                className={UST} data-testid="satis-vade-gun" />
            </div>
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Vade Tarihi</p>
              <Input type="date" value={form.due_date}
                onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))}
                className={UST} data-testid="satis-vade-tarih" />
            </div>
            <div className="col-span-1 md:col-span-1 min-w-0">
              <p className={ALAN_ETIKET}>PB</p>
              <Select value={form.currency}
                onValueChange={(v) => setForm(p => ({ ...p, currency: v, exchange_rate: v === 'TRY' ? '1' : p.exchange_rate }))}>
                <SelectTrigger className={UST} data-testid="satis-pb"><SelectValue /></SelectTrigger>
                <SelectContent>{PARA_BIRIMLERI.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.currency !== 'TRY' && (
              <div className="col-span-1 md:col-span-1 min-w-0">
                <p className={ALAN_ETIKET}>Kur</p>
                <Input type="number" step="any" value={form.exchange_rate}
                  onChange={(e) => setForm(p => ({ ...p, exchange_rate: e.target.value }))}
                  className={UST} data-testid="satis-kur" />
              </div>
            )}
            {/* Depo kalem bazında seçilir; burada yalnızca ÖZET (alışla aynı) */}
            <div className={`${form.currency !== 'TRY' ? 'col-span-2 md:col-span-2' : 'col-span-2 md:col-span-3'} min-w-0`}>
              <p className={ALAN_ETIKET}>Depo</p>
              <div className="h-9 flex items-center text-xs text-[#8a7e70]" data-testid="satis-depo-ozet">
                {kullanilanDepolar.length === 0
                  ? <span className="italic">Kalem bazında seçilir</span>
                  : <span className="text-[#1a1916] font-medium truncate" title={kullanilanDepolar.map(w => w.name).join(', ')}>
                      <Package size={11} className="inline mr-1 -mt-0.5" />
                      {kullanilanDepolar.length === 1 ? kullanilanDepolar[0].name : `${kullanilanDepolar.length} depo`}
                    </span>}
              </div>
            </div>
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>KDV</p>
              <div className="flex h-11 md:h-9 rounded-md border border-[#d0c4b0] overflow-hidden text-sm">
                <button type="button" onClick={() => setForm(p => ({ ...p, kdv_inclusive: false }))}
                  className={`flex-1 text-xs font-medium transition-colors ${!form.kdv_inclusive ? 'bg-[#8a7e70] text-white' : 'bg-white text-[#8a7e70] hover:bg-[#f5f0ea]'}`}
                  data-testid="satis-kdv-haric">Hariç</button>
                <button type="button" onClick={() => setForm(p => ({ ...p, kdv_inclusive: true }))}
                  className={`flex-1 text-xs font-medium transition-colors ${form.kdv_inclusive ? 'bg-[#8a7e70] text-white' : 'bg-white text-[#8a7e70] hover:bg-[#f5f0ea]'}`}
                  data-testid="satis-kdv-dahil">Dahil</button>
              </div>
            </div>
            <div className={`${form.currency !== 'TRY' ? 'col-span-2' : 'col-span-1'} md:col-span-1 min-w-0`}>
              <p className={ALAN_ETIKET}>Not</p>
              <Input value={form.notes} placeholder="Opsiyonel not..."
                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className={UST} data-testid="satis-not" />
            </div>
          </div>
        </div>

        {/* ── Kalemler (alış tablosunun düzeni; Tür sütunu yok) ── */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#d0c4b0]">
            <span className="text-sm font-bold text-[#1a1916]">
              Fatura Kalemleri <span className="text-xs font-normal text-[#8a7e70]">({kalemler.length} kalem)</span>
            </span>
            <Button type="button" size="sm" onClick={kalemEkle}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-7 text-xs px-3"
              data-testid="satis-kalem-ekle"><Plus size={12} className="mr-1" /> Kalem Ekle</Button>
          </div>
          <CardContent className="p-0 md:overflow-x-auto">
            <table className="w-full block md:table md:min-w-[1240px] md:table-fixed">
              <colgroup className="hidden md:table-column-group">
                <col className="w-[230px]" />
                <col />
                <col className="w-[84px]" />
                <col className="w-[84px]" />
                <col className="w-[104px]" />
                <col className="w-[104px]" />
                <col className="w-[68px]" />
                <col className="w-[84px]" />
                <col className="w-[124px]" />
                <col className="w-[108px]" />
                <col className="w-[40px]" />
              </colgroup>
              <thead className="hidden md:table-header-group sticky top-0 z-10">
                <tr className="bg-[#e8dfd0]">
                  <th className={`${BASLIK} text-left`}>Ürün Adı</th>
                  <th className={`${BASLIK} text-left`}>Eşleşme</th>
                  <th className={`${BASLIK} text-right ${GRUP}`} title="Ambalaj adedi">Amb.<br />Adedi</th>
                  <th className={`${BASLIK} text-left`} title="Ambalaj birimi">Amb.<br />Birimi</th>
                  <th className={`${BASLIK} text-right`} title="Bir ambalajın içindeki ürün miktarı">Amb.<br />İçeriği</th>
                  <th className={`${BASLIK} text-right ${GRUP}`}>Birim Fiyat</th>
                  <th className={`${BASLIK} text-right`}>İsk. %</th>
                  <th className={`${BASLIK} text-left ${GRUP}`}>KDV %</th>
                  <th className={`${BASLIK} text-left`}>Depo</th>
                  <th className={`${BASLIK} text-right ${GRUP}`}>Tutar</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {kalemler.map((k, i) => {
                  const tutar = satirTutari(k);
                  const { net: satirNet, kdv: satirKdv } = kdvAyir(tutar.net, k.kdv_rate);
                  const kart = satirKarti(k);
                  const anaBirim = kart?.unit || k.package_unit || '';
                  const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-[#f3eee4]';
                  return (
                    <tr key={i} className={`${rowBg} grid grid-cols-6 gap-x-1.5 gap-y-0.5 md:table-row
                        border border-[#d0c4b0] rounded-lg mb-2 p-2 mx-2
                        md:border-0 md:border-b md:rounded-none md:mb-0 md:p-0 md:mx-0
                        hover:bg-[#e8dfd0]/50 transition-colors`}>
                      {/* Mobilde kartın üstünde satır numarası + tutar */}
                      <td className="md:hidden order-1 col-span-6 flex items-center justify-between mb-1 pb-1 border-b border-[#e6dcc9]">
                        <span className="text-[11px] font-semibold text-[#8a7e70]">
                          Kalem {i + 1}{!k.matched_product_id && ' · hizmet'}
                        </span>
                        <span className="font-mono tabular-nums text-sm font-semibold text-[#1a1916]">{fmtTR(tutar.net)}</span>
                      </td>
                      {/* Ürün Adı — faturaya yazılan metin */}
                      <td className={`${HUCRE} order-2 col-span-6`}>
                        <span className={ETIKET}>Ürün Adı</span>
                        <Input value={k.description} placeholder="Kalem / hizmet açıklaması"
                          onChange={(e) => kalemDegistir(i, 'description', e.target.value)}
                          className={GIRDI} data-testid={`satis-aciklama-${i}`} />
                      </td>
                      {/* Eşleşme — ürün kartı. Boş satır HİZMETTİR, stoka dokunmaz. */}
                      <td className={`${HUCRE} order-3 col-span-6`}>
                        <span className={ETIKET}>Eşleşme</span>
                        <div className="flex items-center gap-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <ProductCombobox value={k.matched_product_id} products={products}
                              placeholder="Ürün seç (hizmet için boş)" emptyText="Ürün bulunamadı"
                              className="h-11 md:h-8 text-[13px]" testId={`satis-urun-${i}`}
                              onChange={(v) => kalemDegistir(i, 'matched_product_id', v)} />
                          </div>
                          {k.matched_product_id && (
                            <button type="button" onClick={() => kalemDegistir(i, 'matched_product_id', '')}
                              title="Eşleşmeyi kaldır (hizmet satırı yap — stoka dokunmaz)"
                              aria-label="Eşleşmeyi kaldır"
                              className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center rounded-md text-[#8a7e70] hover:text-[#7a3535] hover:bg-[#7a3535]/10 shrink-0"
                              data-testid={`satis-eslesme-kaldir-${i}`}><X size={12} /></button>
                          )}
                        </div>
                      </td>
                      {/* Ambalaj Adedi — MİKTAR grubunun başı */}
                      <td className={`${HUCRE} ${GRUP} order-6 col-span-2`}>
                        <span className={ETIKET}>Ambalaj Adedi</span>
                        {/* step="any": sabit adım formu sessizce engelliyor */}
                        <Input type="number" step="any" min="0" value={k.quantity}
                          onChange={(e) => kalemDegistir(i, 'quantity', e.target.value)}
                          placeholder="0" className={SAYI} data-testid={`satis-miktar-${i}`} />
                      </td>
                      {/* Ambalaj Birimi — üründe tanımlı birimler öne çıkar */}
                      <td className={`${HUCRE} order-7 col-span-2`}>
                        <span className={ETIKET}>Ambalaj Birimi</span>
                        {units.length > 0 ? (() => {
                          const tanimli = kart ? birimSecenekleri(kart, units) : [];
                          const tanimliSet = new Set(tanimli.map(o => trNorm(o.deger)));
                          const digerleri = units.filter(u => !tanimliSet.has(trNorm(u.abbreviation || '')));
                          const secenekler = [...tanimli.map(o => o.deger), ...digerleri.map(u => u.abbreviation)];
                          // Listede birebir olmayan kayıtlı değer ("KG") kaybolmasın
                          const kayipDeger = k.unit && !secenekler.includes(k.unit) ? k.unit : null;
                          const bosZorunlu = k.matched_product_id && !(k.unit || '').trim();
                          return (
                            <select value={k.unit || ''} onChange={(e) => kalemDegistir(i, 'unit', e.target.value)}
                              className={`${SECIM} ${bosZorunlu ? 'border-[#7a3535] border-2 bg-[#fff0f0]' : ''}`}
                              title={bosZorunlu ? 'Ambalaj birimini seçin — boş bırakılamaz' : ''}
                              data-testid={`satis-birim-${i}`}>
                              <option value="">—</option>
                              {kayipDeger && <option value={kayipDeger}>{kayipDeger}</option>}
                              {tanimli.length > 0 && (
                                <optgroup label="★ Üründe tanımlı — içeriği otomatik dolar">
                                  {tanimli.map(o => <option key={`t-${o.deger}`} value={o.deger}>★ {o.deger}</option>)}
                                </optgroup>
                              )}
                              {digerleri.length > 0 && (
                                <optgroup label={tanimli.length > 0 ? 'Diğer birimler' : 'Birimler'}>
                                  {digerleri.map(u => <option key={u.id} value={u.abbreviation}>{u.abbreviation}</option>)}
                                </optgroup>
                              )}
                            </select>
                          );
                        })() : (
                          <Input value={k.unit} placeholder="Birim"
                            onChange={(e) => kalemDegistir(i, 'unit', e.target.value)}
                            className={GIRDI} data-testid={`satis-birim-${i}`} />
                        )}
                      </td>
                      {/* Ambalaj İçeriği (ANA BİRİMDE) + stoktan çıkacak toplam */}
                      <td className={`${HUCRE} order-8 col-span-2`}>
                        <span className={ETIKET}>Ambalaj İçeriği</span>
                        {(() => {
                          // Ambalaj birimi ana birimden farklıysa içerik ZORUNLU;
                          // sunucu da reddeder (`_validate_stock_units`).
                          const eksik = k.matched_product_id && anaBirim && k.unit
                            && k.unit.toLowerCase() !== anaBirim.toLowerCase()
                            && !(parseFloat(k.package_qty) > 0);
                          return (
                            <>
                              <div className="flex items-center gap-1.5">
                                <Input type="number" step="any" min="0" value={k.package_qty}
                                  onChange={(e) => kalemDegistir(i, 'package_qty', e.target.value)}
                                  placeholder={eksik ? 'zorunlu' : '0'}
                                  title={eksik ? `1 ${k.unit} kaç ${anaBirim}? Girilmezse stok yanlış birimden düşer.` : ''}
                                  className={`${SAYI} ${eksik ? 'border-[#7a3535] border-2 bg-[#fff0f0]' : ''}`}
                                  data-testid={`satis-icerik-${i}`} />
                                <span className="shrink-0 w-8 text-xs text-[#8a7e70] text-left"
                                  title="Ürünün kayıtlı ana birimi" data-testid={`satis-ana-birim-${i}`}>
                                  {anaBirim || '—'}
                                </span>
                              </div>
                              {eksik && (
                                <span className={`${IPUCU} text-[#7a3535] font-semibold pr-9`}>
                                  1 {k.unit} = ? {anaBirim}
                                </span>
                              )}
                            </>
                          );
                        })()}
                        {k.matched_product_id && parseFloat(k.package_qty) > 0 && parseFloat(k.quantity) > 0 && (
                          <span className={`${IPUCU} text-[#3d6e4e] pr-9`} title="Stoktan çıkacak toplam miktar"
                            data-testid={`satis-stok-cikis-${i}`}>
                            ↳ {((parseFloat(k.quantity) || 0) * parseFloat(k.package_qty)).toLocaleString('tr-TR')} {anaBirim}
                          </span>
                        )}
                      </td>
                      {/* Birim Fiyat — FİYAT grubunun başı (ambalaj fiyatı) */}
                      <td className={`${HUCRE} ${GRUP} order-9 col-span-2`}>
                        <span className={ETIKET}>Birim Fiyat</span>
                        <Input type="number" step="any" min="0" value={k.unit_price}
                          onChange={(e) => kalemDegistir(i, 'unit_price', e.target.value)}
                          placeholder="0,00" className={SAYI} data-testid={`satis-fiyat-${i}`} />
                        {k.matched_product_id && parseFloat(k.package_qty) > 0 && parseFloat(k.package_qty) !== 1 && (
                          <span className={`${IPUCU} text-[#3d6e4e]`} title="Ana birim fiyatı">
                            ↳ {fmtTR((parseFloat(k.unit_price) || 0) / parseFloat(k.package_qty))} /{anaBirim || '?'}
                          </span>
                        )}
                        {parseFloat(k.discount_rate) > 0 && (
                          <span className={`${IPUCU} text-[#3d6e4e]`} title="İskontolu birim fiyat">
                            {fmtTR((parseFloat(k.unit_price) || 0) * (1 - (parseFloat(k.discount_rate) || 0) / 100))}
                          </span>
                        )}
                      </td>
                      <td className={`${HUCRE} order-10 col-span-2`}>
                        <span className={ETIKET}>İskonto %</span>
                        <Input type="number" step="any" min="0" max="100" value={k.discount_rate}
                          onChange={(e) => kalemDegistir(i, 'discount_rate', e.target.value)}
                          placeholder="0" className={SAYI} data-testid={`satis-iskonto-${i}`} />
                      </td>
                      {/* KDV % — kartın oranı varsayılan; satışta karta YAZILMAZ */}
                      <td className={`${HUCRE} ${GRUP} order-11 col-span-2`}>
                        <span className={ETIKET}>KDV %</span>
                        {(() => {
                          const kartOrani = (kart && kart.kdv_rate != null && kart.kdv_rate !== '')
                            ? String(kart.kdv_rate) : null;
                          const deger = k.kdv_rate !== '' && k.kdv_rate != null ? String(k.kdv_rate) : '';
                          const secenekler = [...new Set([...KDV_ORANLARI,
                            ...(kartOrani != null ? [kartOrani] : []), ...(deger ? [deger] : [])])]
                            .sort((a, b) => parseFloat(a) - parseFloat(b));
                          const farkli = kartOrani != null && deger !== '' && parseFloat(deger) !== parseFloat(kartOrani);
                          return (
                            <div>
                              <select value={deger} onChange={(e) => kalemDegistir(i, 'kdv_rate', e.target.value)}
                                title={kartOrani != null
                                  ? `Ürün kartındaki oran %${kartOrani}. Satış faturasında seçilen oran kartı değiştirmez.`
                                  : 'Seçilmezse %0 sayılır'}
                                className={`${SECIM} font-mono tabular-nums ${deger === '' ? 'border-[#e0b45a] text-[#8a7e70]' : farkli ? 'border-[#e0b45a]' : ''}`}
                                data-testid={`satis-kdv-${i}`}>
                                <option value="">— seçin —</option>
                                {secenekler.map(r => (
                                  <option key={r} value={r}>%{r}{r === kartOrani ? ' (kart)' : ''}</option>
                                ))}
                              </select>
                              {farkli && (
                                <span className="block mt-0.5 text-[9px] leading-tight text-[#8a5c00]">kartta %{kartOrani}</span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* Depo — yalnızca ürün kartıyla eşleşen (stoklu) satırda. DOM'da
                          başlıktaki yerinde (KDV'den sonra); mobilde `order-4` ile
                          eşleşmenin altına çıkar (alışla aynı). */}
                      <td className={`${HUCRE} order-4 col-span-6 ${k.matched_product_id ? '' : 'hidden md:table-cell'}`}>
                        <span className={ETIKET}>Depo</span>
                        {k.matched_product_id ? (
                          <select value={k.warehouse_id || ''} onChange={(e) => kalemDegistir(i, 'warehouse_id', e.target.value)}
                            title={k.warehouse_id ? undefined : 'Depo seçilmezse stok çıkışı yapılmaz'}
                            className={`${SECIM} text-xs ${k.warehouse_id ? '' : 'border-[#e0b45a] text-[#8a7e70]'}`}
                            data-testid={`satis-depo-${i}`}>
                            <option value="">— Stok çıkışı yok —</option>
                            {depolar.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                          </select>
                        ) : (
                          <div className="h-8 flex items-center justify-center text-[11px] text-[#c0b8ad]"
                            title="Ürün kartıyla eşleşmeyen satır stoka dokunmaz">stok dışı</div>
                        )}
                      </td>
                      <td className={`${HUCRE} ${GRUP} text-right hidden md:table-cell`}>
                        <span className="font-mono tabular-nums text-[13px] font-semibold text-[#1a1916] leading-8">{fmtTR(tutar.net)}</span>
                        {satirKdv > 0 && (
                          <span className={`${IPUCU} text-[#8a7e70]`}>
                            {form.kdv_inclusive ? `net: ${fmtTR(satirNet)}` : `+${fmtTR(satirKdv)} KDV`}
                          </span>
                        )}
                        {tutar.iskonto > 0 && (
                          <span className={`${IPUCU} text-[#8a7e70]`}>brüt {fmtTR(tutar.brut)}</span>
                        )}
                      </td>
                      <td className="block md:table-cell order-12 col-span-6 px-1 py-1.5 align-top">
                        <Button type="button" variant="ghost" aria-label="Kalemi sil"
                          onClick={() => setKalemler(p => p.filter((_, j) => j !== i))}
                          className="w-full md:w-8 h-10 md:h-8 justify-center text-[#8a7e70] hover:text-[#7a3535] hover:bg-[#7a3535]/10"
                          data-testid={`satis-kalem-sil-${i}`}>
                          <X size={14} /><span className="md:hidden ml-1 text-xs">Kalemi sil</span>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {kalemler.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-8 text-[#8a7e70] text-sm">
                    Henüz kalem yok — "Kalem Ekle" ile başlayın
                  </td></tr>
                )}
              </tbody>
            </table>
            {kalemler.length > 0 && (
              <div className="border-t border-[#d0c4b0] bg-[#f5f0e8]">
                <div className="flex justify-end p-4">
                  <div className="space-y-1.5 min-w-[260px]" data-testid="satis-toplamlar">
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>İskontosuz Toplam</span>
                      <span className="font-mono">{fmtTR(brutToplam)} {form.currency}</span>
                    </div>
                    {iskontoToplam > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-[#7a3535]">
                          <span>İskonto</span>
                          <span className="font-mono">-{fmtTR(iskontoToplam)} {form.currency}</span>
                        </div>
                        <div className="flex justify-between text-sm text-[#8a7e70] border-t border-[#d0c4b0] pt-1.5">
                          <span>Net Toplam</span>
                          <span className="font-mono">{fmtTR(brutToplam - iskontoToplam)} {form.currency}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>KDV Matrahı</span>
                      <span className="font-mono">{fmtTR(matrah)} {form.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>KDV Tutarı</span>
                      <span className="font-mono">{fmtTR(kdvToplam)} {form.currency}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-[#1a1916] border-t border-[#d0c4b0] pt-1.5 mt-1.5">
                      <span>Genel Toplam</span>
                      <span className="font-mono">{fmtTR(genelToplam)} {form.currency}</span>
                    </div>
                    {form.currency !== 'TRY' && parseFloat(form.exchange_rate) > 0 && (
                      <div className="flex justify-between text-xs text-[#8a7e70]">
                        <span>TL Karşılığı</span>
                        <span className="font-mono">{fmtTR(genelToplam * parseFloat(form.exchange_rate || 1))} TL</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {yuklemeAcik && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setYuklemeAcik(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-[#1a1916]">Satış Belgesi Yükle</h2>
                <button type="button" onClick={() => setYuklemeAcik(false)}
                  className="text-[#8a7e70] hover:text-[#1a1916]"><X size={18} /></button>
              </div>
              <div
                onDragOver={e => { e.preventDefault(); setSurukleniyor(true); }}
                onDragLeave={() => setSurukleniyor(false)}
                onDrop={e => {
                  e.preventDefault();
                  setSurukleniyor(false);
                  const dosya = e.dataTransfer.files?.[0];
                  if (dosya) dosyaOku(null, dosya);
                }}
                onClick={() => dosyaRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors
                  ${surukleniyor ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#d0c4b0] hover:border-[#8a7e70] bg-[#faf7f2]'}`}
                data-testid="satis-birak-alani">
                <Upload size={32} className={surukleniyor ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'} />
                <p className="text-sm font-semibold text-[#1a1916]">Dosyayı buraya sürükle</p>
                <p className="text-xs text-[#8a7e70]">veya tıklayarak seç</p>
                <p className="text-[10px] text-[#b0a090]">PNG, JPG, PDF desteklenir (en çok 5 sayfa)</p>
              </div>
            </div>
          </div>
        )}

        {gorselAcik && gorsel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setGorselAcik(false)}>
            <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={e => e.stopPropagation()}>
              <button type="button" onClick={() => setGorselAcik(false)}
                className="absolute -top-10 right-0 text-white hover:text-gray-300"><X size={24} /></button>
              <img src={gorsel} alt="Satış belgesi"
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" />
            </div>
          </div>
        )}

        <div className="hidden md:flex justify-end gap-3">
          <Button type="button" variant="outline" className="border-[#d0c4b0]" onClick={geri}>İptal</Button>
          <Button type="button" variant="outline" disabled={kaydediliyor}
            className="border-[#d0c4b0]" onClick={() => kaydet('draft')}
            data-testid="satis-taslak-kaydet">Taslak Kaydet</Button>
          <Button type="button" disabled={kaydediliyor}
            className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
            onClick={() => kaydet('completed')} data-testid="satis-kaydet">
            <Save size={16} className="mr-2" />
            {kaydediliyor ? 'Kaydediliyor...' : 'Faturayı Kaydet'}
          </Button>
        </div>
      </div>

      {/* Mobilde yapışkan alt çubuk: toplam + kaydet her zaman erişilebilir (alışla aynı) */}
      <div className="md:hidden sticky bottom-0 -mx-4 mt-3 px-4 py-2 bg-[#faf7f2]
                      border-t border-[#d0c4b0] flex items-center gap-2 z-30">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-[#8a7e70] uppercase leading-none">Genel Toplam</p>
          <p className="font-mono tabular-nums text-base font-bold text-[#1a1916] truncate">
            {fmtTR(genelToplam)} {form.currency}
          </p>
        </div>
        <Button type="button" variant="outline" disabled={kaydediliyor}
          className="border-[#d0c4b0] h-11" onClick={() => kaydet('draft')}>Taslak</Button>
        <Button type="button" disabled={kaydediliyor}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold h-11"
          onClick={() => kaydet('completed')}>
          <Save size={16} className="mr-1.5" />{kaydediliyor ? '...' : 'Kaydet'}
        </Button>
      </div>
    </div>
  );
}
